import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Cake } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { apiGet, apiPut } from "@/lib/api";
import { formatDateOnly } from "@/lib/date-only";

export const Route = createFileRoute("/_authenticated/admin/birthdays")({
  component: BirthdaysPage,
});

interface Settings {
  greetWorkers: boolean;
  greetEmail: boolean;
  greetSms: boolean;
  noteSuperAdmins: boolean;
  noteDivAdmins: boolean;
  noteSupervisors: boolean;
}
interface Data {
  settings: Settings;
  upcoming: { id: string; name: string; division: string | null; date: string }[];
}

function BirthdaysPage() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "birthdays"],
    queryFn: () => apiGet<{ data: Data }>("/api/admin/birthday-settings"),
  });
  const save = useMutation({
    mutationFn: (patch: Partial<Settings>) => apiPut<{ data: { settings: Settings } }>("/api/admin/birthday-settings", patch),
    onSuccess: (r) => {
      qc.setQueryData(["admin", "birthdays"], (old: { data: Data } | undefined) => (old ? { data: { ...old.data, settings: r.data.settings } } : old));
      toast.success("Saved");
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const s = data?.data.settings;
  const upcoming = data?.data.upcoming ?? [];

  const row = (id: string, label: string, hint: string, key: keyof Settings, disabled = false) => (
    <div className="flex items-start justify-between gap-4 py-3">
      <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </label>
      <Switch id={id} checked={!!s?.[key]} disabled={disabled || save.isPending} onCheckedChange={(v) => save.mutate({ [key]: v })} />
    </div>
  );

  return (
    <div className="mx-auto max-w-2xl space-y-5 pb-10">
      <div className="space-y-1">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-text-primary"><Cake className="h-5 w-5" /> Birthdays</h2>
        <p className="text-sm text-muted-foreground">Everything is off until you switch it on. Notes go out once a day, at 8:00 AM Pacific, on the day of the birthday. They never show a person's age.</p>
      </div>

      {isLoading && <Skeleton className="h-48 w-full rounded-xl" />}
      {isError && <p className="text-sm text-destructive">This page is for super admins.</p>}

      {s && (
        <>
          <Card>
            <CardContent className="divide-y px-4 py-1">
              <p className="pt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Happy Birthday to the worker</p>
              {row("bd-greet", "Send a Happy Birthday message", "To everyone on the Paris team (not subcontractors). Always in the app, with a phone notification if they allowed it.", "greetWorkers")}
              {row("bd-email", "Also send it by email", "Only when the message above is on.", "greetEmail", !s.greetWorkers)}
              {row("bd-sms", "Also send it by text", "Only when the message above is on. Needs a phone number on the profile.", "greetSms", !s.greetWorkers)}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="divide-y px-4 py-1">
              <p className="pt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Crew birthday note</p>
              <p className="py-2 text-xs text-muted-foreground">A short note in the app naming who has a birthday today. Each person sees their own division's crew; super admins see everyone.</p>
              {row("bd-sa", "Super admins", "All birthdays in the company.", "noteSuperAdmins")}
              {row("bd-da", "Division admins", "The birthdays in their division.", "noteDivAdmins")}
              {row("bd-sup", "Supervisors", "The birthdays in the division they supervise.", "noteSupervisors")}
            </CardContent>
          </Card>

          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Coming up (next 30 days)</p>
            {upcoming.length === 0 ? (
              <p className="text-sm text-muted-foreground">No birthdays in the next 30 days.</p>
            ) : (
              <ul className="divide-y rounded-lg border bg-card">
                {upcoming.map((u) => (
                  <li key={`${u.id}-${u.date}`} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                    <span className="min-w-0 truncate font-medium">{u.name}{u.division ? <span className="font-normal text-muted-foreground"> · {u.division}</span> : null}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{formatDateOnly(u.date, undefined, { month: "short", day: "numeric" })}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
